import { call, takeLeading, select, put } from 'redux-saga/effects';
import {
  CREATE_RECURRING_TASK_REQUEST,
  createRecurringTaskFailureAction,
  CreateRecurringTaskRequestAction,
  createRecurringTaskSuccessAction,
} from '../actions';
import { enqueueNotification } from 'store/modules/ui';
import { makeTaskManagementApi } from 'services/TaskManagementApi/TaskManagementApi';
import { ICreateRecurringTaskRequest, ITaskReminder } from 'services/TaskManagementApi/types/TaskManagementResponse';
import { recurrenceFormToRequest, RECURRING_TASKS_PATH } from 'containers/TaskManagement/RecurringTasks/recurrence';
import { duplicatedReminderIdsSelector, listTaskRemindersSelector } from '../selectors';
import { getRecurringTaskErrorMessages } from '../helpers';
import { IRecurringTaskFormValues } from 'containers/TaskManagement/taskFormValidation';

// OWA-11923. Same body shape as a task, minus everything a template doesn't have, plus the rule.
// OWA-11997: the update body is the same shape.
export const recurringTaskFormToRequest = (
  task: IRecurringTaskFormValues,
  taskReminders: ITaskReminder[]
): ICreateRecurringTaskRequest => ({
  subject: task.subject,
  description: task.description || null,
  priority: task.priority,
  categoryId: task.category ?? '',
  teamId: task.teamId ?? '',
  assigneeId: !task.assigneeId || task.assigneeId === 'all' ? null : task.assigneeId,
  reviewerId: task.reviewerId || null,
  // Before the deadline, like a task's: the tasks created from the template copy them.
  reminders: taskReminders.map(item => ({
    channel: item.channel,
    offsetUnit: item.offsetUnit,
    offset: -Math.abs(item.offset),
  })),
  restrictedToTeamIds: task.restrictedToTeamIds ?? [],
  recurrence: recurrenceFormToRequest(task.recurrence),
  // OWA-12096. Always sent, `null` when empty: on PUT a missing key would keep the saved value.
  bookingHumanReadableId: task.bookingRef?.trim() || null,
  crmDirectoryEntryId: task.crmDirectoryEntryId || null,
  crmDirectoryEntryFullName: task.crmDirectoryEntryFullName || null,
});

/** TMS's INVALID_RECURRENCE message, shown under the Recurring Setup section rather than as a toast. */
export const getRecurrenceError = (e: any): string | null => {
  const errors: { message: string; errorCode: string }[] = e?.response?.data?.errors ?? [];
  return errors.find(error => error.errorCode === 'INVALID_RECURRENCE')?.message ?? null;
};

/** Toasts for a failed create / update that isn't an INVALID_RECURRENCE. */
export function* notifyRecurringTaskSaveError(e: any, fallbackMessage: string) {
  const errorMessages = getRecurringTaskErrorMessages(e);
  for (const errorMessage of errorMessages.length ? errorMessages : [fallbackMessage]) {
    yield put(enqueueNotification({ message: errorMessage, options: { variant: 'error' } }));
  }
}

export function* createRecurringTaskSaga(action: CreateRecurringTaskRequestAction) {
  try {
    const duplicatedReminders = yield select(duplicatedReminderIdsSelector);
    if (duplicatedReminders?.length) {
      yield put(createRecurringTaskFailureAction('Check duplicated reminders'));
      return;
    }

    const taskReminders: ITaskReminder[] = yield select(listTaskRemindersSelector);
    const recurringTaskToCreate = recurringTaskFormToRequest(action.task, taskReminders);

    const taskManagementApi = makeTaskManagementApi();
    yield call(taskManagementApi.createRecurringTask, recurringTaskToCreate);
    yield put(createRecurringTaskSuccessAction());
    yield put(
      enqueueNotification({ message: 'Recurring task created successfully', options: { variant: 'success' } })
    );
    yield call(action.history.push, RECURRING_TASKS_PATH);
  } catch (e) {
    const recurrenceError = getRecurrenceError(e);
    yield put(createRecurringTaskFailureAction(e, recurrenceError));
    if (!recurrenceError) {
      yield* notifyRecurringTaskSaveError(e, 'Error creating recurring task');
    }
  }
}

// takeLeading, not takeLatest: cancelling the saga doesn't cancel the POST, so a second Save landing
// before the button disables (Formik validates async) would create a duplicate template.
export function* watchCreateRecurringTaskSaga() {
  yield takeLeading([CREATE_RECURRING_TASK_REQUEST], createRecurringTaskSaga);
}
